/* eslint-disable no-magic-numbers */
import {
  allBioEntitiesWithTypeOfCurrentMapSelector,
  allVisibleBioEntitiesIdsSelector,
} from '@/redux/bioEntity/bioEntity.selectors';
import { entityNumberDataSelector } from '@/redux/entityNumber/entityNumber.selectors';
import { ModelElement } from '@/types/models';
import { usePointToProjection } from '@/utils/map/usePointToProjection';
import Feature from 'ol/Feature';
import { Geometry } from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getBioEntitiesFeatures } from './getBioEntitiesFeatures';
import { getMultipinsModelElements } from './getMultipinsModelElements';
import { getMultipinBioEntititesIds } from './getMultipinsBioEntitiesIds';
import { getMultipinFeatures } from './getMultipinsFeatures';

/**
 * Builds the shared {@link VectorSource} holding the bio-entity pin features
 * (single pins, multi-pins and marker pins) derived from the Redux store.
 *
 * Map-independent so it can be rendered by several maps; wrap it with
 * `createPinsLayer`.
 */
export const useOlMapPinsSource = (): VectorSource<Feature<Geometry>> => {
  const pointToProjection = usePointToProjection();
  const activeIds = useSelector(allVisibleBioEntitiesIdsSelector);
  const modelElements = useSelector(allBioEntitiesWithTypeOfCurrentMapSelector);
  const entityNumber = useSelector(entityNumberDataSelector);
  const multiPinsBioEntities = useMemo(
    () =>
      getMultipinsModelElements({
        modelElements,
      }),
    [modelElements],
  );
  const multipinsIds = getMultipinBioEntititesIds(multiPinsBioEntities);
  const isMultiPin = useCallback(
    (b: ModelElement): boolean => multipinsIds.includes(b.id),
    [multipinsIds],
  );

  const elementsFeatures = useMemo(() => {
    return [
      getBioEntitiesFeatures(
        modelElements.filter(b => !isMultiPin(b)),
        {
          pointToProjection,
          entityNumber,
          activeIds,
        },
      ),
      getMultipinFeatures(multiPinsBioEntities, {
        pointToProjection,
        entityNumber,
        activeIds,
      }),
    ].flat();
  }, [modelElements, pointToProjection, entityNumber, activeIds, multiPinsBioEntities, isMultiPin]);

  const vectorSource = useMemo(() => new VectorSource<Feature<Geometry>>(), []);

  useEffect(() => {
    vectorSource.clear();
    vectorSource.addFeatures(elementsFeatures);
  }, [elementsFeatures, vectorSource]);

  return vectorSource;
};
